/**
 * BookingWizard — 5-step wrapper.
 *
 * Owns the reducer state, step transitions, and deflection modal. Each step
 * renders the shared `BookingRail` + its step body. The footer stepper lives
 * in the rail, not the body, so the body can scroll independently if needed.
 *
 * Server wiring: fire-and-forget `dispatch({type:'setConfirmation'})` today —
 * when PLANNED `/v3/bookings/*` ships, `onSubmit` will POST to `/v3/bookings`
 * and capture the server-issued code.
 */

import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { BookingRail } from './BookingRail';
import { Step1Service } from './Step1Service';
import { Step2Method } from './Step2Method';
import { Step3Details } from './Step3Details';
import { Step4Documents } from './Step4Documents';
import { Step5YourDetails } from './Step5YourDetails';
import { Confirmation } from './Confirmation';
import { ApplyOnlineIntervention } from './ApplyOnlineIntervention';
import { useBookingWizard, generateConfirmationCode } from '../wizardState';
import type { WizardStepIndex } from '../types';

type Phase = WizardStepIndex | 'confirmed';

export function BookingWizard(): JSX.Element {
  const { state, dispatch, canAdvanceTo } = useBookingWizard();
  const [phase, setPhase] = useState<Phase>(0);
  const [showDeflection, setShowDeflection] = useState<boolean>(false);
  const navigate = useNavigate();

  const advance = useCallback(
    (next: WizardStepIndex) => {
      if (canAdvanceTo(next)) setPhase(next);
    },
    [canAdvanceTo],
  );

  const submit = useCallback(() => {
    // TODO Wave 3: replace with `apiClient.portal.bookings.create(...)` once the
    // `/portal/bookings` (or `/v3/bookings`) route lands (api-wiring digest §3).
    const code = generateConfirmationCode();
    dispatch({
      type: 'setConfirmation',
      confirmationNumber: code,
      officerDisplayName: 'Ella Monroe',
      officerInitials: 'EM',
    });
    setPhase('confirmed');
  }, [dispatch]);

  if (phase === 'confirmed') {
    return (
      <Confirmation
        state={state}
        onBookAnother={() => { dispatch({ type: 'reset' }); setPhase(0); }}
        onReschedule={() => setPhase(2)}
        onCancel={() => { dispatch({ type: 'reset' }); setPhase(0); }}
      />
    );
  }

  return (
    <>
      <div className="rae-wizard">
        <BookingRail currentStep={phase} state={state} />
        {phase === 0 && (
          <Step1Service
            state={state}
            dispatch={dispatch}
            onContinue={() => advance(1)}
          />
        )}
        {phase === 1 && (
          <Step2Method
            state={state}
            dispatch={dispatch}
            onContinue={() => advance(2)}
            onBack={() => advance(0)}
          />
        )}
        {phase === 2 && (
          <Step3Details
            state={state}
            dispatch={dispatch}
            onContinue={() => advance(3)}
            onBack={() => advance(1)}
            onDeflect={() => setShowDeflection(true)}
          />
        )}
        {phase === 3 && (
          <Step4Documents
            state={state}
            dispatch={dispatch}
            onContinue={() => advance(4)}
            onBack={() => advance(2)}
          />
        )}
        {phase === 4 && (
          <Step5YourDetails
            state={state}
            dispatch={dispatch}
            onSubmit={submit}
            onBack={() => advance(3)}
          />
        )}
      </div>

      <ApplyOnlineIntervention
        open={showDeflection}
        onClose={() => setShowDeflection(false)}
        onApplyOnline={() => {
          setShowDeflection(false);
          navigate('/rcp/apply');
        }}
      />
    </>
  );
}
